/**
 * Time Sync Deep Links
 *
 * OS-specific deep links to system time settings for clock synchronization.
 * Used by MFA components when clock skew is detected.
 */

/**
 * Get OS-specific deep link to time settings
 *
 * Returns a deep link URL that opens the device's time/date settings.
 * Deep links may not work in all browsers due to security restrictions.
 *
 * @returns Deep link URL or null if OS not detected/supported
 */
export function getTimeSyncLink(): string | null {
  const ua = navigator.userAgent;

  // iOS (iPhone, iPad)
  if (/iPhone|iPad|iPod/.test(ua)) {
    // iOS Settings deep link
    return 'App-Prefs:General&path=DATE_AND_TIME';
  }

  // Android
  if (/Android/.test(ua)) {
    // Android Settings intent
    return 'intent://settings/date_time#Intent;end';
  }

  // macOS
  if (/Mac/.test(ua) && !/iPhone|iPad|iPod/.test(ua)) {
    // macOS System Preferences deep link
    return 'x-apple.systempreferences:com.apple.preference.datetime';
  }

  // Windows
  if (/Win/.test(ua)) {
    // Windows Settings deep link
    return 'ms-settings:dateandtime';
  }

  // Linux or other OS - no standard deep link
  return null;
}

/**
 * Get user-friendly OS name for display
 *
 * @returns OS name string (e.g., "iOS", "Android", "macOS", "Windows")
 */
export function getOSName(): string {
  const ua = navigator.userAgent;

  if (/iPhone|iPad|iPod/.test(ua)) return 'iOS';
  if (/Android/.test(ua)) return 'Android';
  if (/Mac/.test(ua) && !/iPhone|iPad|iPod/.test(ua)) return 'macOS';
  if (/Win/.test(ua)) return 'Windows';
  if (/Linux/.test(ua)) return 'Linux';

  return 'your device';
}

/**
 * Get instructions for manually opening time settings
 *
 * Returns human-readable instructions for users whose browsers
 * don't support deep links.
 *
 * @returns Instruction string
 */
export function getTimeSyncInstructions(): string {
  const os = getOSName();

  switch (os) {
    case 'iOS':
      return 'Open Settings → General → Date & Time and enable "Set Automatically"';
    case 'Android':
      return 'Open Settings → System → Date & Time and enable "Set time automatically"';
    case 'macOS':
      return 'Open System Preferences → Date & Time and enable "Set date and time automatically"';
    case 'Windows':
      return 'Open Settings → Time & Language → Date & Time and enable "Set time automatically"';
    default:
      return 'Open your system settings and enable automatic time synchronization';
  }
}
